import {useState} from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import * as LucideIcons from "lucide-react";

const SolutionOverview = () => {
    const [activeModule, setActiveModule] = useState('citizen');

    const modules = [
        {
            id: 'citizen',
            name: 'Citizen Portal',
            icon: 'Users',
            color: 'primary',
            description: 'Self-service payment platform for all assembly services',
            features: [
                'Property rates and business permit payments',
                'Real-time payment confirmation',
                'Digital receipt generation',
                'Payment history tracking',
                'Multiple payment methods (Mobile Money, Bank Cards)',
                'Ghana Card integration for verification'
            ],
            mockup: {
                title: 'Pay Assembly Services',
                services: [
                    {name: 'Property Rates', amount: '₵450.00', status: 'Due'},
                    {name: 'Business Permit Renewal', amount: '₵1,200.00', status: 'Paid'},
                    {name: 'Market Toll (Monthly)', amount: '₵75.00', status: 'Due'}
                ]
            }
        },
        {
            id: 'collector',
            name: 'Revenue Collector App',
            icon: 'Smartphone',
            color: 'success',
            description: 'Mobile app for field revenue collection with offline sync',
            features: [
                'Offline payment collection capability',
                'GPS location tracking for collections',
                'Digital receipt printing via Bluetooth',
                'Automatic sync when online',
                'Collector performance analytics',
                'Secure login with biometric authentication'
            ],
            mockup: {
                title: 'Field Collection',
                collections: [
                    {type: 'Market Toll', amount: '₵25.00', location: 'Kejetia Market'},
                    {type: 'Parking Fee', amount: '₵15.00', location: 'Central Business District'},
                    {type: 'Permit Fee', amount: '₵300.00', location: 'Industrial Area'}
                ]
            }
        },
        {
            id: 'dashboard',
            name: 'Assembly Dashboard',
            icon: 'BarChart3',
            color: 'accent',
            description: 'Real-time analytics and reporting for assembly management',
            features: [
                'Real-time revenue tracking',
                'Collector performance monitoring',
                'Revenue stream analysis',
                'Compliance reporting for government',
                'Automated financial reconciliation',
                'Customizable KPI dashboards'
            ],
            mockup: {
                title: 'Revenue Analytics',
                metrics: [
                    {label: 'Today\'s Collection', value: '₵45,230', change: '+12%'},
                    {label: 'Monthly Target', value: '₵850,000', progress: 68},
                    {label: 'Active Collectors', value: '24', status: 'online'}
                ]
            }
        },
        {
            id: 'gateway',
            name: 'Payment Gateway',
            icon: 'CreditCard',
            color: 'secondary',
            description: 'Secure payment processing with multiple provider integration',
            features: [
                'MTN Mobile Money integration',
                'Vodafone Cash support',
                'AirtelTigo Money connectivity',
                'Bank card processing',
                'Instant payment confirmation',
                'Fraud detection and prevention'
            ],
            mockup: {
                title: 'Payment Methods',
                methods: [
                    {name: 'MTN Mobile Money', status: 'Active', transactions: '2,847'},
                    {name: 'Vodafone Cash', status: 'Active', transactions: '1,923'},
                    {name: 'Bank Cards', status: 'Active', transactions: '456'}
                ]
            }
        },
        {
            id: 'analytics',
            name: 'Analytics Suite',
            icon: 'TrendingUp',
            color: 'warning',
            description: 'Advanced reporting and business intelligence tools',
            features: [
                'Revenue forecasting models',
                'Citizen behavior analytics',
                'Collection efficiency reports',
                'Compliance audit trails',
                'Custom report generation',
                'Data export capabilities'
            ],
            mockup: {
                title: 'Performance Insights',
                insights: [
                    {metric: 'Revenue Growth', value: '+340%', period: 'vs last year'},
                    {metric: 'Collection Efficiency', value: '98.2%', period: 'this month'},
                    {metric: 'Digital Adoption', value: '87%', period: 'citizen usage'}
                ]
            }
        }
    ];

    const currentModule = modules?.find(m => m?.id === activeModule);

    return (
        <section className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section Header */}
                <div className="text-center mb-16">
                    <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
                        Complete Digital Ecosystem for Local Assemblies
                    </h2>
                    <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                        Five integrated applications working together to transform your revenue collection
                        and service delivery operations with full offline capabilities.
                    </p>
                </div>

                {/* Module Navigation */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-12">
                    {modules?.map((module) => (
                        <button
                            key={module.id}
                            onClick={() => setActiveModule(module.id)}
                            className={`p-6 rounded-xl border-2 transition-all duration-300 text-center ${
                                activeModule === module.id
                                    ? `border-${module.color} bg-${module.color}/5`
                                    : 'border-border hover:border-muted-foreground bg-muted/30'
                            }`}
                        >
                            <div className={`w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-3 ${
                                activeModule === module.id
                                    ? `bg-${module.color} text-white`
                                    : 'bg-muted text-muted-foreground'
                            }`}>
                                <Icon name={module.icon as keyof typeof LucideIcons} size={24}/>
                            </div>
                            <h3 className={`font-semibold text-sm ${
                                activeModule === module.id ? `text-${module.color}` : 'text-muted-foreground'
                            }`}>
                                {module.name}
                            </h3>
                        </button>
                    ))}
                </div>

                {/* Active Module Details */}
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                    {/* Module Information */}
                    <div className="space-y-8">
                        <div>
                            <div className="flex items-center space-x-3 mb-4">
                                <div
                                    className={`w-16 h-16 bg-${currentModule?.color}/10 rounded-xl flex items-center justify-center`}>
                                    <Icon name={currentModule?.icon as keyof typeof LucideIcons} size={32}
                                          color={`var(--color-${currentModule?.color})`}/>
                                </div>
                                <div>
                                    <h3 className="text-2xl font-bold text-foreground">{currentModule?.name}</h3>
                                    <p className="text-muted-foreground">{currentModule?.description}</p>
                                </div>
                            </div>

                            {/* Offline Capability Badge */}
                            <div
                                className="inline-flex items-center space-x-2 px-3 py-1 bg-success/10 rounded-full border border-success/20 mb-6">
                                <Icon name="Wifi" size={16} color="var(--color-success)"/>
                                <span className="text-sm font-medium text-success">Offline Capable</span>
                            </div>
                        </div>

                        {/* Features List */}
                        <div className="space-y-4">
                            <h4 className="text-lg font-semibold text-foreground">Key Features</h4>
                            <div className="grid gap-3">
                                {currentModule?.features?.map((feature, index) => (
                                    <div key={index} className="flex items-start space-x-3">
                                        <Icon name="Check" size={16} color="var(--color-success)"
                                              className="mt-1 flex-shrink-0"/>
                                        <span className="text-muted-foreground">{feature}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Ghana Card Integration */}
                        <div className="bg-primary/5 rounded-lg p-6 border border-primary/20">
                            <div className="flex items-center space-x-3 mb-3">
                                <Icon name="CreditCard" size={20} color="var(--color-primary)"/>
                                <h4 className="font-semibold text-primary">Ghana Card Integration</h4>
                            </div>
                            <p className="text-sm text-muted-foreground">
                                Seamlessly integrates with Ghana's national ID system for secure citizen verification
                                and streamlined service delivery.
                            </p>
                        </div>
                    </div>

                    {/* Interactive Mockup */}
                    <div className="relative">
                        <div className="bg-slate-900 rounded-3xl p-4 shadow-2xl">
                            <div className="bg-white rounded-2xl overflow-hidden h-96">
                                {/* App Header */}
                                <div className={`bg-${currentModule?.color} p-4 text-white`}>
                                    <div className="flex items-center justify-between">
                                        <h3 className="font-semibold">{currentModule?.mockup?.title}</h3>
                                        <Icon name="MoreVertical" size={20}/>
                                    </div>
                                </div>

                                {/* App Content */}
                                <div className="p-4 space-y-4 h-full overflow-y-auto">
                                    {currentModule?.id === 'citizen' && (
                                        <div className="space-y-3">
                                            {currentModule?.mockup?.services?.map((service, index) => (
                                                <div key={index}
                                                     className="flex items-center justify-between p-3 bg-muted rounded-lg">
                                                    <div>
                                                        <p className="font-medium text-sm">{service?.name}</p>
                                                        <p className="text-xs text-muted-foreground">{service?.status}</p>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="font-bold text-primary">{service?.amount}</p>
                                                        <Button size="xs"
                                                                variant={service?.status === 'Paid' ? 'outline' : 'default'}>
                                                            {service?.status === 'Paid' ? 'Paid' : 'Pay Now'}
                                                        </Button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {currentModule?.id === 'collector' && (
                                        <div className="space-y-3">
                                            {currentModule?.mockup?.collections?.map((collection, index) => (
                                                <div key={index}
                                                     className="flex items-center justify-between p-3 bg-muted rounded-lg">
                                                    <div>
                                                        <p className="font-medium text-sm">{collection?.type}</p>
                                                        <p className="text-xs text-muted-foreground">{collection?.location}</p>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="font-bold text-success">{collection?.amount}</p>
                                                        <div className="flex items-center space-x-1">
                                                            <Icon name="MapPin" size={12}
                                                                  color="var(--color-muted-foreground)"/>
                                                            <span className="text-xs text-muted-foreground">GPS</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {currentModule?.id === 'dashboard' && (
                                        <div className="space-y-3">
                                            {currentModule?.mockup?.metrics?.map((metric, index) => (
                                                <div key={index} className="p-3 bg-muted rounded-lg">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <p className="text-sm font-medium">{metric?.label}</p>
                                                        {metric?.change && (
                                                            <span
                                                                className="text-xs text-success font-medium">{metric?.change}</span>
                                                        )}
                                                    </div>
                                                    <p className="text-lg font-bold text-primary">{metric?.value}</p>
                                                    {metric?.progress && (
                                                        <div className="mt-2">
                                                            <div className="w-full bg-border rounded-full h-2">
                                                                <div
                                                                    className="bg-primary h-2 rounded-full"
                                                                    style={{width: `${metric?.progress}%`}}
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {currentModule?.id === 'gateway' && (
                                        <div className="space-y-3">
                                            {currentModule?.mockup?.methods?.map((method, index) => (
                                                <div key={index}
                                                     className="flex items-center justify-between p-3 bg-muted rounded-lg">
                                                    <div>
                                                        <p className="font-medium text-sm">{method?.name}</p>
                                                        <p className="text-xs text-success">{method?.status}</p>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="font-bold text-secondary">{method?.transactions}</p>
                                                        <p className="text-xs text-muted-foreground">transactions</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {currentModule?.id === 'analytics' && (
                                        <div className="space-y-3">
                                            {currentModule?.mockup?.insights?.map((insight, index) => (
                                                <div key={index} className="p-3 bg-muted rounded-lg">
                                                    <p className="text-sm font-medium mb-1">{insight?.metric}</p>
                                                    <div className="flex items-center justify-between">
                                                        <p className="text-xl font-bold text-warning">{insight?.value}</p>
                                                        <p className="text-xs text-muted-foreground">{insight?.period}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Connection Lines */}
                        <div
                            className="absolute -top-4 -right-4 w-8 h-8 bg-success rounded-full flex items-center justify-center">
                            <Icon name="Wifi" size={16} color="white"/>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default SolutionOverview;